import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { api } from '@/lib/api';
import { TRACK_LABELS } from '@/lib/tracks';
import { toast } from 'sonner';

export default function ChapterPickerPage() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const track = search.get('track');
  const isPractical = track === 'BOARDS_PRACTICAL';
  const [syllabus, setSyllabus] = useState(null);
  const [klass, setKlass] = useState('12');
  const [subject, setSubject] = useState('');
  const [chapter, setChapter] = useState('');
  const [practicals, setPracticals] = useState([]);

  useEffect(() => {
    if (!track) {
      nav('/dashboard');
      return;
    }
    api.get('/syllabus').then(({ data }) => setSyllabus(data));
  }, [track, nav]);

  const subjectsForTrack = useMemo(() => {
    if (!syllabus || !track) return [];
    return syllabus.track_subjects[track] || [];
  }, [syllabus, track]);

  useEffect(() => {
    if (subjectsForTrack.length && !subjectsForTrack.includes(subject)) {
      setSubject(subjectsForTrack[0]);
    }
  }, [subjectsForTrack, subject]);

  // For BOARDS_PRACTICAL, fetch practicals list; else fetch NCERT chapters
  useEffect(() => {
    if (!subject) return;
    setChapter('');
    if (isPractical) {
      api
        .get('/practicals', { params: { klass: Number(klass), subject } })
        .then(({ data }) => {
          setPracticals(data.practicals || []);
          if ((data.practicals || []).length) {
            setChapter(data.practicals[0].id);
          }
        })
        .catch(() => setPracticals([]));
    } else {
      setPracticals([]);
    }
  }, [isPractical, klass, subject]);

  const chapters = useMemo(() => {
    if (isPractical) return practicals.map((p) => ({ value: p.id, label: p.name }));
    if (!syllabus || !subject) return [];
    return (syllabus.syllabus?.[klass]?.[subject] || []).map((c) => ({ value: c, label: c }));
  }, [isPractical, practicals, syllabus, klass, subject]);

  useEffect(() => {
    if (!isPractical && chapters.length && !chapters.find((c) => c.value === chapter)) {
      setChapter(chapters[0].value);
    }
  }, [isPractical, chapters, chapter]);

  const start = () => {
    if (!track || !subject || !chapter) {
      toast.error('Please select all fields');
      return;
    }
    const params = new URLSearchParams({
      track,
      klass,
      subject,
      chapter,
    });
    nav(`/chapter?${params.toString()}`);
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="chapter-picker-page">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Track</div>
          <h1 className="font-display text-3xl font-semibold">{TRACK_LABELS[track] || track}</h1>
          <p className="mt-1 text-muted-foreground">Pick your class, subject, chapter, and set.</p>
        </div>

        <Card>
          <CardContent className="grid gap-4 p-6 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Class</Label>
              <Select value={klass} onValueChange={setKlass}>
                <SelectTrigger data-testid="class-select" className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="11">Class 11</SelectItem>
                  <SelectItem value="12">Class 12</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Subject</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger data-testid="subject-select" className="h-11">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent>
                  {subjectsForTrack.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{isPractical ? 'Practical' : 'Chapter'}</Label>
              <Select value={chapter} onValueChange={setChapter}>
                <SelectTrigger data-testid="chapter-select" className="h-11">
                  <SelectValue placeholder={isPractical ? 'Select practical' : 'Select chapter'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {chapters.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end sm:col-span-2">
              <Button className="h-11 w-full gap-1.5" onClick={start} data-testid="continue-to-setup-button">
                {isPractical ? 'Open practical →' : 'Open chapter →'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Button variant="ghost" onClick={() => nav(-1)} data-testid="picker-back-button">
          ← Back
        </Button>
      </div>
    </AppShell>
  );
}
